import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const theme = createTheme({
  palette: {
    primary: { main: "#1C2541" },
    secondary: { main: "#EE8013" },
  },
});

const BlogPost = () => {
  const { slug } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const lang = i18n.language?.split("-")[0] || "en";

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`https://app.autocrescente.com/WhyNotApi/api/blog/slug/${slug}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((data) => setPost(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  // Update page meta when post loads
  useEffect(() => {
    if (!post) return;
    const c = post[lang]?.title ? post[lang] : post.en || {};
    if (c.metaTitle) document.title = c.metaTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    if (c.metaDescription) meta.setAttribute("content", c.metaDescription);
    return () => { document.title = "Why Not Car Rental"; };
  }, [post, lang]);

  const getContent = () => {
    if (!post) return {};
    const c = post[lang];
    if (c?.title) return c;
    return post.en || {};
  };

  const getCoverUrl = () =>
    post?.coverImage
      ? `https://app.autocrescente.com/WhyNotApi/uploads/blog/${post.coverImage}`
      : null;

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress sx={{ color: "#1C2541" }} />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box textAlign="center" py={14}>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Article not found
        </Typography>
        <Link
          component="button"
          onClick={() => navigate("/blog")}
          sx={{ color: "#1C2541", fontWeight: "bold", mt: 2, display: "inline-block" }}
        >
          ← Back to Blog
        </Link>
      </Box>
    );
  }

  const content = getContent();
  const cover = getCoverUrl();

  return (
    <ThemeProvider theme={theme}>
      {/* Hero cover */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 260, md: 440 },
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#1C2541",
        }}
      >
        {cover && (
          <Box
            component="img"
            src={cover}
            alt={content.title || ""}
            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(to bottom, transparent 30%, rgba(28,37,65,0.72) 100%)",
          }}
        />
        {/* Title overlay on hero */}
        <Container
          maxWidth="md"
          sx={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            pb: { xs: 3, md: 5 },
          }}
        >
          {post.tags?.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              {post.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: "#EE8013",
                    color: "white",
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
              ))}
            </Stack>
          )}
          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            sx={{ color: "white", lineHeight: 1.2, textShadow: "0 2px 8px rgba(0,0,0,0.3)" }}
          >
            {content.title}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component="button"
            underline="hover"
            onClick={() => navigate("/")}
            sx={{ fontSize: 14, color: "text.secondary", background: "none", border: "none", cursor: "pointer" }}
          >
            Home
          </Link>
          <Link
            component="button"
            underline="hover"
            onClick={() => navigate("/blog")}
            sx={{ fontSize: 14, color: "text.secondary", background: "none", border: "none", cursor: "pointer" }}
          >
            Blog
          </Link>
          <Typography color="text.primary" fontSize={14} noWrap sx={{ maxWidth: 260 }}>
            {content.title}
          </Typography>
        </Breadcrumbs>

        {/* Summary / subtitle */}
        {content.summary && (
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ fontWeight: 400, lineHeight: 1.6, mb: 2 }}
          >
            {content.summary}
          </Typography>
        )}

        {/* Date */}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 4 }}>
          {formatDate(post.publishedAt)}
        </Typography>

        <Divider sx={{ mb: 5 }} />

        {/* Article HTML content */}
        <Box
          component="article"
          sx={{
            "& h1": { fontSize: "2rem", fontWeight: "bold", color: "#1C2541", mt: 4, mb: 1.5 },
            "& h2": { fontSize: "1.5rem", fontWeight: "bold", color: "#1C2541", mt: 4, mb: 1.5 },
            "& h3": { fontSize: "1.2rem", fontWeight: "bold", color: "#1C2541", mt: 3, mb: 1 },
            "& h4, & h5, & h6": { fontWeight: "bold", color: "#1C2541", mt: 2.5, mb: 1 },
            "& p": { lineHeight: 1.85, mb: 2, color: "#333", fontSize: "1.05rem" },
            "& ul, & ol": { pl: 3, mb: 2 },
            "& li": { mb: 0.75, lineHeight: 1.8, color: "#333", fontSize: "1.05rem" },
            "& img": { maxWidth: "100%", borderRadius: 2, my: 3, display: "block" },
            "& a": { color: "#EE8013", textDecoration: "underline", "&:hover": { color: "#c96a00" } },
            "& blockquote": {
              borderLeft: "4px solid #EE8013",
              pl: 3,
              ml: 0,
              color: "#555",
              fontStyle: "italic",
              my: 3,
              py: 0.5,
            },
            "& code": {
              backgroundColor: "#f5f5f5",
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "0.9em",
            },
            "& pre": {
              backgroundColor: "#f5f5f5",
              p: 2.5,
              borderRadius: 2,
              overflow: "auto",
              my: 3,
              "& code": { background: "none", p: 0 },
            },
            "& strong": { color: "#1C2541", fontWeight: 700 },
            "& hr": { my: 4, border: "none", borderTop: "1px solid #e0e0e0" },
          }}
          dangerouslySetInnerHTML={{ __html: content.content }}
        />

        <Divider sx={{ mt: 7, mb: 4 }} />

        {/* Back to blog */}
        <Box
          component="button"
          onClick={() => navigate("/blog")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "none",
            border: "none",
            color: "#1C2541",
            fontWeight: "bold",
            cursor: "pointer",
            fontSize: 15,
            p: 0,
            "&:hover": { color: "#EE8013" },
            transition: "color 0.15s",
          }}
        >
          <ArrowBackIcon fontSize="small" />
          {t("backToBlog", "Back to Blog")}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default BlogPost;
